import axios from 'axios';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { BASE_API_URL } from '../helpers/Config';
import toast from 'react-hot-toast';

const SupportScreen = ({ theme = 'dark' }) => {
    const [activeFaq, setActiveFaq] = useState(null);
    const [SupportLinks, setSupportLinks] = useState([]);

    const isDark = theme === 'dark';
    const styles = getStyles(isDark);
    const headingClass = isDark ? 'text-light' : 'text-dark';
    const mutedClass = isDark ? 'text-light-emphasis' : 'text-muted';
    const cardBorderClass = isDark ? '' : 'border';

    const FetchSupportDetails = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/company/support`);
            const data = response?.data;

            if (data?.data) {
                const general = data?.data.find((item) => item.type === 'general')?.link;
                const payment = data?.data.find((item) => item.type === 'payment')?.link;
                const technical = data?.data.find((item) => item.type === 'technical')?.link;
                const emergency = data?.data.find((item) => item.type === 'emergency')?.link;

                setSupportLinks([
                    {
                        name: "General Support",
                        username: general,
                        description: "For general inquiries and account issues",
                        icon: "💬",
                        responseTime: "Within 2 hours"
                    },
                    {
                        name: "Technical Support",
                        username: technical,
                        description: "For technical issues and bug reports",
                        icon: "🔧",
                        responseTime: "Within 4 hours"
                    },
                    {
                        name: "Billing Support",
                        username: payment,
                        description: "For payment and withdrawal issues",
                        icon: "💰",
                        responseTime: "Within 1 hour"
                    },
                    {
                        name: "Emergency Support",
                        username: emergency,
                        description: "For urgent security issues",
                        icon: "🚨",
                        responseTime: "Within 30 minutes"
                    }
                ]);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to load support details');
        }
    };

    useEffect(() => {
        FetchSupportDetails();
    }, []);

    const supportConditions = [
        "Support available 24/7 for all registered users",
        "Please have your User ID ready when contacting support",
        "Response time may vary during peak hours",
        "For security, never share your password with support staff",
        "Keep your conversation professional and respectful"
    ];

    const faqs = [
        {
            question: "What information should I provide when contacting support?",
            answer: "Please provide your User ID, the issue you're facing, steps to reproduce, and any relevant screenshots."
        },
        {
            question: "How long does it take to get a response?",
            answer: "Response times vary by support type. General inquiries: 2 hours, Technical: 4 hours, Billing: 1 hour, Emergency: 30 minutes."
        },
        {
            question: "Is Telegram support secure?",
            answer: "Yes, all our Telegram handlers are verified and encrypted. Never share sensitive information like passwords."
        },
        {
            question: "What languages are supported?",
            answer: "We currently support English, Spanish, and Chinese. More languages will be added soon."
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    const cardHoverVariants = {
        hover: {
            scale: 1.02,
            transition: {
                duration: 0.2
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="container-fluid px-3 py-4"
            style={styles.container}
        >
            {/* Header */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-center mb-4"
            >
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="me-2" style={{ fontSize: '1.8rem' }}>💁</span>
                    <h4 className={`mb-0 ${headingClass}`}>Support Center</h4>
                </div>
                <p className={`${mutedClass} mb-0`} style={{ fontSize: '0.9rem' }}>
                    Get help via Telegram - Choose the right support channel
                </p>
            </motion.div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Telegram Support Section */}
                <motion.div variants={itemVariants} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                        <span className="me-2" style={{ fontSize: '1.3rem' }}>📱</span>
                        <h5 className={`mb-0 ${headingClass}`}>Telegram Support</h5>
                    </div>

                    <div className="row g-3">
                        {SupportLinks.map((handler, index) => (
                            <motion.div
                                key={index}
                                className="col-12"
                                variants={itemVariants}
                                whileHover="hover"
                            >
                                <motion.div
                                    variants={cardHoverVariants}
                                    className={`card ${cardBorderClass}`}
                                    style={styles.card}
                                >
                                    <div className="card-body">
                                        <div className="d-flex align-items-start">
                                            <div className="me-3" style={{ fontSize: '1.5rem' }}>
                                                {handler.icon}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className="card-title mb-1" style={styles.titleText}>
                                                    {handler.name}
                                                </h6>
                                                <p className="card-text mb-1">
                                                    <strong>@{handler.username}</strong>
                                                </p>
                                                <p
                                                    className={`card-text mb-1 ${mutedClass}`}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    {handler.description}
                                                </p>
                                                <small className="text-primary">
                                                    ⏱️ Response: {handler.responseTime}
                                                </small>
                                            </div>
                                            <motion.button
                                                whileTap={{ scale: 0.95 }}
                                                className="btn btn-primary btn-sm"
                                                onClick={() =>
                                                    window.open(
                                                        `https://t.me/${handler.username?.replace('@', '')}`,
                                                        '_blank'
                                                    )
                                                }
                                            >
                                                Contact
                                            </motion.button>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Support Conditions */}
                <motion.div variants={itemVariants} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                        <span className="me-2" style={{ fontSize: '1.3rem' }}>📋</span>
                        <h5 className={`mb-0 ${headingClass}`}>Support Guidelines</h5>
                    </div>

                    <motion.div className={`card ${cardBorderClass}`} style={styles.card}>
                        <div className="card-body">
                            <ul className="list-unstyled mb-0">
                                {supportConditions.map((condition, index) => (
                                    <motion.li
                                        key={index}
                                        className="d-flex align-items-start mb-2"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <span className="text-success me-2">✓</span>
                                        <span style={{ fontSize: '0.85rem' }}>{condition}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={itemVariants}>
                    <div className="d-flex align-items-center mb-3">
                        <span className="me-2" style={{ fontSize: '1.3rem' }}>❓</span>
                        <h5 className={`mb-0 ${headingClass}`}>Frequently Asked Questions</h5>
                    </div>

                    <div className={`card ${cardBorderClass}`} style={styles.card}>
                        <div className="card-body p-0">
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    className="border-bottom"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <button
                                        className="btn w-100 text-start p-3 d-flex justify-content-between align-items-center"
                                        onClick={() =>
                                            setActiveFaq(activeFaq === index ? null : index)
                                        }
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: isDark ? '#e5e7eb' : '#111827'
                                        }}
                                    >
                                        <span
                                            style={{ fontSize: '0.9rem' }}
                                            className="fw-medium"
                                        >
                                            {faq.question}
                                        </span>
                                        <motion.span
                                            animate={{
                                                rotate: activeFaq === index ? 180 : 0
                                            }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            ▼
                                        </motion.span>
                                    </button>
                                    <motion.div
                                        initial={false}
                                        animate={{
                                            height: activeFaq === index ? 'auto' : 0,
                                            opacity: activeFaq === index ? 1 : 0
                                        }}
                                        transition={{ duration: 0.3 }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <div
                                            className="px-3 pb-3"
                                            style={{ fontSize: '0.85rem' }}
                                        >
                                            <p className={`${mutedClass} mb-0`}>{faq.answer}</p>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Quick Tips */}
                <motion.div variants={itemVariants} className="mt-4">
                    <div
                        className="card border-primary"
                        style={styles.tipCard}
                    >
                        <div className="card-body text-center">
                            <div className="mb-2" style={{ fontSize: '1.5rem' }}>💡</div>
                            <h6 className="card-title text-primary">Quick Tip</h6>
                            <p
                                className={`${mutedClass} card-text`}
                                style={{ fontSize: '0.85rem' }}
                            >
                                For faster support, choose the right Telegram handler and provide
                                clear details about your issue.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

const getStyles = (isDark) => ({
    container: {
        maxWidth: '480px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: isDark ? '#020617' : 'transparent',
        color: isDark ? '#e5e7eb' : '#111827'
    },
    card: {
        backgroundColor: isDark ? '#020617' : '#ffffff',
        borderRadius: '12px',
        border: isDark ? '1px solid #1f2937' : '1px solid #dee2e6',
        color: isDark ? '#e5e7eb' : '#111827'
    },
    tipCard: {
        backgroundColor: isDark ? '#020617' : '#e7f1ff',
        borderRadius: '12px',
        border: isDark ? '1px solid #1d4ed8' : '1px solid #0d6efd',
        color: isDark ? '#e5e7eb' : '#111827'
    },
    titleText: {
        color: isDark ? '#e5e7eb' : '#111827'
    }
});

export default SupportScreen;
